// Dawn-style Variant Selector
class VariantSelector {
  constructor() {
    this.product = null;
    this.currentVariant = null;
    this.variantIdInput = null;
    this.addToCartButton = null;
    this.outOfStockContainer = null;
    this.addToCartContainer = null;
    this.priceElement = null;
    this.lastVariantId = null;
    this.priceUpdateTimeout = null;
    this.lastPriceUpdateVariantId = null;
    
    this.init();
  }

  init() {
    if (!this.isProductPage()) {
      console.log('🛍️ Variant Selector: not on a product page, skipping initialization');
      return;
    }

    this.loadProductData()
      .then(() => {
        this.setupVariantInputs();
        this.setupEventListeners();

        // Prefer variant from URL, else from hidden input, else compute from current UI
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariantId = urlParams.get('variant');
        if (urlVariantId && this.product?.variants?.some(v => v.id.toString() === urlVariantId.toString())) {
          this.selectVariantById(urlVariantId);
        } else if (this.variantIdInput?.value) {
          // Sync UI controls to the server-rendered default variant
          this.selectVariantById(this.variantIdInput.value);
        } else {
          this.updateVariant();
        }
      })
      .catch((error) => {
        console.warn('⚠️ Variant Selector: initialization skipped due to product data error:', error);
      });
  }

  isProductPage() {
    return /^\/products\/[^/]+/.test(window.location.pathname);
  }

  async loadProductData() {
    try {
      // Ensure we are on a product page and extract the handle
      const match = window.location.pathname.match(/^\/products\/([^/]+)/);
      if (!match) {
        console.log('🛍️ Not on a product page; product data will not be loaded');
        return;
      }

      const productHandle = match[1];
      const response = await fetch(`/products/${productHandle}.js`, {
        headers: { Accept: 'application/json' }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status} while fetching product JSON`);
      }

      const text = await response.text();
      if (!text) {
        throw new Error('Empty product JSON response');
      }

      try {
        this.product = JSON.parse(text);
      } catch (parseError) {
        throw new Error('Invalid product JSON');
      }

      console.log('🛍️ Product data loaded:', this.product);
    } catch (error) {
      console.error('❌ Error loading product data:', error);
      this.product = null;
      throw error; // propagate so init can decide to skip wiring up
    }
  }

  setupVariantInputs() {
    this.variantIdInput = document.getElementById('product-variant-id');
    this.addToCartButton = document.getElementById('add-to-cart-btn');
    this.outOfStockContainer = document.getElementById('out-of-stock-container');
    this.addToCartContainer = document.getElementById('add-to-cart-container');
    
    // Find price element (you may need to adjust this selector)
    this.priceElement = document.querySelector('.product-header_price, .product-price, [data-price]');
    
    console.log('🛍️ Variant inputs found:', {
      variantIdInput: this.variantIdInput,
      addToCartButton: this.addToCartButton,
      outOfStockContainer: this.outOfStockContainer,
      addToCartContainer: this.addToCartContainer
    });

    // Initialize lastVariantId
    if (this.variantIdInput) {
      this.lastVariantId = this.variantIdInput.value;
    }
  }

  setupEventListeners() {
    // Listen for variant option changes using a more robust approach
    this.setupVariantChangeListener();
    
    // Ensure add-to-cart functionality works
    this.ensureAddToCartWorks();
  }

  setupVariantChangeListener() {
    // Listen for changes to the variant ID input (updated by radio handler)
    const variantIdInput = document.getElementById('product-variant-id');
    if (variantIdInput) {
      variantIdInput.addEventListener('change', () => {
        console.log('🛍️ Variant ID input changed:', variantIdInput.value);
        this.updateVariantFromId(variantIdInput.value);
      });
      
      // Also listen for direct input changes
      variantIdInput.addEventListener('input', () => {
        console.log('🛍️ Variant ID input changed (input event):', variantIdInput.value);
        this.updateVariantFromId(variantIdInput.value);
      });
    }
    
    // Listen for custom variantChange event from add-to-cart block
    document.addEventListener('variantChange', (event) => {
      console.log('🛍️ VariantChange event received:', event.detail);
      if (event.detail && event.detail.variantId) {
        // Longer delay to ensure StarApps has finished updating visibility and DOM has stabilized
        setTimeout(() => {
          this.updateVariantFromId(event.detail.variantId);
        }, 300);
      }
    });
    
    // Listen for radio button changes directly as backup
    document.querySelectorAll('.product-header_radio input[type="radio"]').forEach(radio => {
      radio.addEventListener('change', () => {
        console.log('🛍️ Radio button changed:', radio.name, '=', radio.value);
        // Small delay to let the radio handler update the variant ID first
        setTimeout(() => {
          this.updateVariant();
        }, 100);
      });
    });

    // Listen for select dropdown changes (e.g., Size)
    document.querySelectorAll('select.product-variant-option').forEach(select => {
      select.addEventListener('change', () => {
        console.log('🛍️ Select changed:', select.name, '=', select.value);
        setTimeout(() => {
          this.updateVariant();
        }, 50);
      });
    });
  }

  updateVariantFromId(variantId) {
    if (!this.product) return;
    
    const variant = this.product.variants.find(v => v.id.toString() === variantId.toString());
    if (variant) {
      console.log('🛍️ Found variant from ID:', variant);
      this.currentVariant = variant;
      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateAddToCartButton(variant);
      // Navigate gallery to variant images (StarApps filters visibility, but we need to navigate)
      this.updateProductImage(variant);
      this.updateVariantId(variant.id);
      this.updateUrl(variant.id);
    }
  }

  ensureAddToCartWorks() {
    // Make sure the form submission is handled by existing logic
    const forms = document.querySelectorAll('form[action*="/cart/add"]');
    forms.forEach(form => {
      // Remove any existing event listeners we might have added
      form.removeEventListener('submit', this.handleFormSubmit);
      
      // Add a submit event listener with capture phase to ensure variant ID is updated BEFORE form submission
      // This ensures the form always has the correct variant ID when submitted
      form.addEventListener('submit', (e) => {
        this.ensureVariantIdBeforeSubmit(e);
      }, true); // Use capture phase to run before other handlers
      
      console.log('🛍️ Form found, ensuring variant ID is updated before submission');
    });
  }

  ensureVariantIdBeforeSubmit(event) {
    // Ensure variant ID is updated synchronously before form submission
    if (!this.product || !this.variantIdInput) {
      return;
    }

    // Get current variant ID from the hidden input
    const currentVariantId = this.variantIdInput.value;
    
    // If we have a current variant set, ensure it matches the hidden input
    if (this.currentVariant) {
      const expectedVariantId = this.currentVariant.id.toString();
      if (currentVariantId !== expectedVariantId) {
        console.log('🛍️ Synchronizing variant ID before form submission:', expectedVariantId);
        // Update synchronously without triggering events to avoid loops
        this.variantIdInput.value = expectedVariantId;
        this.variantIdInput.setAttribute('data-variant-id', expectedVariantId);
        this.lastVariantId = expectedVariantId;
      }
    } else {
      // If no current variant, try to compute it from selected options
      const selectedOptions = this.getSelectedOptions();
      const variant = this.findVariant(selectedOptions);
      if (variant) {
        const variantId = variant.id.toString();
        if (currentVariantId !== variantId) {
          console.log('🛍️ Computing and updating variant ID before form submission:', variantId);
          this.variantIdInput.value = variantId;
          this.variantIdInput.setAttribute('data-variant-id', variantId);
          this.lastVariantId = variantId;
          this.currentVariant = variant;
        }
      }
    }
  }

  updateVariant() {
    if (!this.product) {
      console.log('🛍️ Product data not loaded yet, skipping variant update');
      return;
    }

    const selectedOptions = this.getSelectedOptions();
    console.log('🛍️ Selected options:', selectedOptions);

    // Find matching variant
    let variant = this.findVariant(selectedOptions);
    console.log('🛍️ Found variant:', variant);

    if (variant) {
      this.currentVariant = variant;
      this.updateVariantId(variant.id);
      this.updateUrl(variant.id);
      this.updatePrice(variant);
      this.updateAvailability(variant);
      this.updateAddToCartButton(variant);
      // Navigate gallery to variant images (StarApps filters visibility, but we need to navigate)
      this.updateProductImage(variant);
      this.updateOptionAvailability();
    } else {
      // Fallback: try hidden input's variant ID if set by other scripts
      const fallbackId = this.variantIdInput?.value || this.variantIdInput?.getAttribute('data-variant-id');
      if (fallbackId) {
        console.log('🛍️ No match from options, falling back to hidden input variant ID:', fallbackId);
        this.updateVariantFromId(fallbackId);
      } else {
        console.log('🛍️ No matching variant found and no fallback variant ID');
        this.handleNoVariant();
      }
    }
  }

  getSelectedOptions() {
    const options = {};
    
    // Get selected options from radio inputs
    const radioInputs = document.querySelectorAll('.product-header_radio input[type="radio"]:checked');
    radioInputs.forEach(input => {
      options[input.name] = input.value;
    });

    // Get selected options from variant selects
    const selectInputs = document.querySelectorAll('select.product-variant-option');
    selectInputs.forEach(select => {
      if (select.value !== undefined && select.value !== null && select.value !== '') {
        options[select.name] = select.value;
      }
    });

    // Also capture any checked inputs with class product-variant-option (fallback)
    const variantInputs = document.querySelectorAll('.product-variant-option:checked');
    variantInputs.forEach(input => {
      options[input.name] = input.value;
    });

    return options;
  }

  findVariant(selectedOptions) {
    const optionNames = (this.product.options || []).map((opt) => {
      return typeof opt === 'string' ? opt : (opt && opt.name ? opt.name : opt);
    });

    return this.product.variants.find(variant => {
      const values = Array.isArray(variant.options)
        ? variant.options
        : [variant.option1, variant.option2, variant.option3].filter(v => v !== undefined);

      return values.every((value, index) => {
        const optionName = optionNames[index];
        if (!optionName) return true;
        // Allow partial selections: if user hasn't chosen this option yet, treat as wildcard
        if (selectedOptions[optionName] === undefined) return true;
        return selectedOptions[optionName] === value;
      });
    });
  }

  updateVariantId(variantId) {
    if (this.variantIdInput) {
      const newId = variantId.toString();
      const oldId = (this.variantIdInput.value || '').toString();
      if (oldId !== newId) {
        this.variantIdInput.value = newId;
        this.variantIdInput.setAttribute('data-variant-id', newId);
        this.lastVariantId = newId;
        document.querySelector('[data-selected-variant]').setAttribute('data-selected-variant', newId);
        console.log('🛍️ Updated variant ID:', newId);
        // Notify other scripts that rely on this input
        try {
          this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
          this.variantIdInput.dispatchEvent(new Event('input', { bubbles: true }));
        } catch (e) {}
      }
    }
  }

  updateUrl(variantId) {
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      // Remove any hash fragment so we don't end the URL with '#'
      url.hash = '';
      window.history.replaceState({}, '', url.toString());
      console.log('🔗 Updated URL with variant:', variantId);
    } catch (e) {
      console.warn('⚠️ Failed to update URL with variant', e);
    }
  }

  selectVariantById(variantId) {
    if (!this.product) return;
    const variant = this.product.variants.find(v => v.id.toString() === variantId.toString());
    if (!variant) return;

    // Sync UI controls to this variant's options
    variant.options.forEach((value, index) => {
      const optionName = this.product.options[index].name;

      // Select dropdowns
      const selectEl = document.querySelector(`select.product-variant-option[name="${optionName}"]`);
      if (selectEl) {
        selectEl.value = value;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Radios
      const radios = document.querySelectorAll(`.product-header_radio input[type="radio"][name="${optionName}"]`);
      radios.forEach(r => {
        if (r.value === value) {
          r.checked = true;
          r.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    // Ensure internal state/UI is updated
    this.updateVariantFromId(variantId);
  }

  updatePrice(variant) {
    // Prevent duplicate updates for the same variant
    const variantId = variant.id.toString();
    if (this.lastPriceUpdateVariantId === variantId) {
      return;
    }
    
    // Clear any pending price updates
    if (this.priceUpdateTimeout) {
      clearTimeout(this.priceUpdateTimeout);
    }
    
    // Debounce the price update to prevent flicker from rapid changes
    this.priceUpdateTimeout = setTimeout(() => {
      // Use requestAnimationFrame for smooth updates without flicker
      requestAnimationFrame(() => {
        // Update the main price element using ID for better performance
        const mainPriceEl = document.getElementById('product-main-price');
        if (mainPriceEl) {
          mainPriceEl.textContent = this.formatMoney(variant.price).replace('.00', '');
        } else {
          // Fallback to selector if ID doesn't exist
          const fallbackEl = document.querySelector('.product-header_price-wrapper .text-size-large.text-weight-bold:not(.text-style-strikethrough)');
          if (fallbackEl) {
            fallbackEl.textContent = this.formatMoney(variant.price).replace('.00', '');
          }
        }
        
        // Update compare at price using ID
        const comparePriceEl = document.getElementById('product-compare-price');
        const comparePriceContainer = document.getElementById('product-compare-price-container');
        
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          // Show compare at price
          if (comparePriceEl && comparePriceContainer) {
            comparePriceEl.textContent = this.formatMoney(variant.compare_at_price).replace('.00', '');
            comparePriceContainer.style.display = 'block';
          } else {
            // Fallback: try selector approach
            const fallbackCompareEl = document.querySelector('.product-header_price-wrapper .text-style-strikethrough');
            const fallbackContainer = fallbackCompareEl ? fallbackCompareEl.closest('div') : null;
            if (fallbackCompareEl) {
              fallbackCompareEl.textContent = this.formatMoney(variant.compare_at_price).replace('.00', '');
            }
            if (fallbackContainer) {
              fallbackContainer.style.display = 'block';
            }
          }
        } else {
          // Hide compare at price
          if (comparePriceContainer) {
            comparePriceContainer.style.display = 'none';
          } else {
            // Fallback: try selector approach
            const fallbackCompareEl = document.querySelector('.product-header_price-wrapper .text-style-strikethrough');
            const fallbackContainer = fallbackCompareEl ? fallbackCompareEl.closest('div') : null;
            if (fallbackContainer) { 
              fallbackContainer.style.display = 'none';
            }
          }
        }
        
        // Update discount percentage and offer tag
        this.updateOfferTag(variant);
        
        // Mark this variant as updated
        this.lastPriceUpdateVariantId = variantId;

        // Installments line removed from all PDPs (2026-09-09); guard so its absence never breaks this callback
        const paymentTerms = document.querySelector('shopify-payment-terms');
        if (paymentTerms) paymentTerms.setAttribute('variant-id', variantId);
        
        console.log('🛍️ Updated price:', this.formatMoney(variant.price));
      });
    }, 0);
  }
  
  updateOfferTag(variant) {
    const offerTag = document.getElementById('product-offer-tag') || document.querySelector('.offer-tag');
    
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      const discountPercentage = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
      
      if (offerTag) {
        offerTag.textContent = `Save ${discountPercentage}%`;
        offerTag.style.display = 'block';
      } else {
        // Create offer tag if it doesn't exist
        const priceWrapper = document.querySelector('.product-header_price-wrapper');
        if (priceWrapper) {
          const offerDiv = document.createElement('div');
          offerDiv.className = 'offer-tag';
          offerDiv.id = 'product-offer-tag';
          offerDiv.textContent = `Save ${discountPercentage}%`;
          priceWrapper.appendChild(offerDiv);
        }
      }
    } else {
      // Hide offer tag
      if (offerTag) {
        offerTag.style.display = 'none';
      }
    }
  }

  updateProductImage(variant) {

var variantId = variant.id;

var $li = $('.varinat_dtat li[varinat_id="' + variantId + '"]');
if ($li.length) {
var variantUrl = $li.attr('varinat_url');

if (variantUrl) {
  $('.product_thumbnail-wrapper[data-media-url="' + variantUrl + '"]').trigger('click');
}
    }


    // Find the swiper instance
    const swiperContainer = document.querySelector('.swiper.is-product-gallery');
    if (!swiperContainer) {
      console.log('🖼️ Swiper container not found');
      return;
    }

    // Get swiper instance
    const swiper = swiperContainer.swiper || window.swiperInstance;
    if (!swiper || typeof swiper.slideTo !== 'function') {
      console.log('🖼️ Swiper instance not found');
      return;
    }

    // Look for an image slide that includes this variant ID
    const wrapper = swiperContainer.querySelector('.swiper-wrapper');
    if (!wrapper) {
      console.log('🖼️ Swiper wrapper not found');
      return;
    }

    // Get all slides and find the one matching the variant
    const slides = Array.from(wrapper.querySelectorAll('.swiper-slide.is-product-gallery'));
    let targetSlide = null;
    let fallbackSlide = null; // For universal images (no variant IDs)

    for (const slide of slides) {
      // Check if slide is visible (not hidden by StarApps or CSS)
      const isVisible = slide.offsetParent !== null && 
                        window.getComputedStyle(slide).display !== 'none' &&
                        window.getComputedStyle(slide).visibility !== 'hidden' &&
                        window.getComputedStyle(slide).opacity !== '0';
      
      if (!isVisible) continue; // Skip hidden slides
      
      const variantIds = slide.getAttribute('data-variant-ids');
      
      // If no variant IDs specified, image applies to all variants - use as fallback
      if (!variantIds || variantIds.trim() === '') {
        // Store first universal image as fallback
        if (!fallbackSlide) {
          fallbackSlide = slide;
        }
      } else {
        // Check if variant ID is in the comma-separated list
        const variantIdList = variantIds.split(',').map(id => id.trim());
        if (variantIdList.includes(variant.id.toString())) {
          // This is a specific match for this variant - use it
          targetSlide = slide;
          console.log('🖼️ Found matching visible image slide for variant:', variant.id);
          break; // Found specific match, no need to continue
        }
      }
    }

    // If no specific match found, use fallback (universal image)
    if (!targetSlide && fallbackSlide) {
      targetSlide = fallbackSlide;
      console.log('🖼️ Using universal image slide (applies to all variants)');
    }

    if (targetSlide) {
      // Wait for StarApps to finish updating visibility before navigating
      // Use requestAnimationFrame to ensure DOM is stable
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Double-check that target slide is still visible after StarApps updates
          const isStillVisible = targetSlide.offsetParent !== null && 
                                  window.getComputedStyle(targetSlide).display !== 'none' &&
                                  window.getComputedStyle(targetSlide).visibility !== 'hidden' &&
                                  window.getComputedStyle(targetSlide).opacity !== '0';
          
          if (!isStillVisible) {
            console.log('🖼️ Target slide is no longer visible, finding new target');
            // Re-find the target slide in case StarApps changed visibility
            const variantIdList = variant.id.toString();
            const allSlides = Array.from(wrapper.querySelectorAll('.swiper-slide.is-product-gallery'));
            for (const slide of allSlides) {
              const slideVariantIds = slide.getAttribute('data-variant-ids');
              const isVisible = slide.offsetParent !== null && 
                                window.getComputedStyle(slide).display !== 'none' &&
                                window.getComputedStyle(slide).visibility !== 'hidden' &&
                                window.getComputedStyle(slide).opacity !== '0';
              
              if (isVisible && slideVariantIds && slideVariantIds.includes(variantIdList)) {
                targetSlide = slide;
                break;
              }
            }
          }
          
          // Swiper uses DOM index directly - get the index from the DOM order
          // Get all slides in DOM order (including hidden ones)
          const allSlides = Array.from(wrapper.children).filter(el => el.classList.contains('swiper-slide') && el.classList.contains('is-product-gallery'));
          const slideIndex = allSlides.indexOf(targetSlide);
          
          if (slideIndex !== -1 && slideIndex >= 0) {
            swiper.slideTo(slideIndex, 0); // 0 = no animation, instant change
            console.log('🖼️ Switched to slide:', slideIndex, 'for variant:', variant.id);
          } else {
            console.log('🖼️ Could not determine slide index for target slide');
          }
        });
      });
    } else {
      console.log('🖼️ No matching visible image found for variant:', variant.id);
    }
  }

  updateAvailability(variant) {
    const isAvailable = variant.available;

    if (this.addToCartContainer && this.outOfStockContainer) {
      if (isAvailable) {
        // Clear inline display to respect stylesheet layout (flex/grid)
        this.addToCartContainer.style.display = '';
        this.outOfStockContainer.style.display = 'none';
      } else {
        this.addToCartContainer.style.display = 'none';
        this.outOfStockContainer.style.display = '';
      }
    }

    console.log('🛍️ Updated availability:', isAvailable);
  }

  // Recompute per-option availability across ALL axes given the current
  // selection. Without this, the cross-axis disabled state baked by Liquid
  // at page render goes stale on every axis change (e.g. L-shape desks:
  // change Return Side, Size list stays frozen until refresh).
  updateOptionAvailability() {
    if (!this.product || !Array.isArray(this.product.variants)) return;
    const selected = this.getSelectedOptions();
    const optionNames = (this.product.options || []).map((opt) => {
      return typeof opt === 'string' ? opt : (opt && opt.name ? opt.name : opt);
    });
    if (!optionNames.length) return;

    const variantValues = (v) => Array.isArray(v.options)
      ? v.options
      : [v.option1, v.option2, v.option3].filter((x) => x !== undefined);

    optionNames.forEach((optName, axisIndex) => {
      const candidateValues = new Set();
      this.product.variants.forEach((v) => {
        const vals = variantValues(v);
        if (vals[axisIndex] !== undefined) candidateValues.add(vals[axisIndex]);
      });

      candidateValues.forEach((val) => {
        const hasAvailable = this.product.variants.some((v) => {
          if (!v.available) return false;
          const vals = variantValues(v);
          if (vals[axisIndex] !== val) return false;
          return optionNames.every((n, i) => {
            if (i === axisIndex) return true;
            if (selected[n] === undefined) return true;
            return selected[n] === vals[i];
          });
        });

        // 1. <select> <option> — mirror Liquid: native disabled + variant_hide attribute
        const selectEl = document.querySelector(`select.product-variant-option[name="${CSS.escape(optName)}"]`);
        if (selectEl) {
          Array.prototype.forEach.call(selectEl.options, (opt) => {
            if (opt.value !== val) return;
            if (hasAvailable) {
              opt.disabled = false;
              opt.removeAttribute('variant_hide');
            } else {
              opt.disabled = true;
              opt.setAttribute('variant_hide', '');
            }
          });
        }

        // 2. <input type="radio"> + .product-header_radio-button sibling — mirror Liquid's `data-disabled` class
        const radio = document.querySelector(`.product-header_radio input[type="radio"][name="${CSS.escape(optName)}"][value="${CSS.escape(val)}"]`);
        if (radio) {
          radio.classList.toggle('data-disabled', !hasAvailable);
          radio.disabled = !hasAvailable;
          const buttonDiv = radio.previousElementSibling;
          if (buttonDiv && buttonDiv.classList.contains('product-header_radio-button')) {
            buttonDiv.classList.toggle('data-disabled', !hasAvailable);
          }
        }
      });
    });
  }

  updateAddToCartButton(variant) {
    if (this.addToCartButton) {
      const addToCartText = this.addToCartButton.querySelector('.add-to-cart-text');
      if (variant.available) {
        this.addToCartButton.disabled = false;
        if (addToCartText) {
          addToCartText.textContent = 'Add to cart';
        } else {
          this.addToCartButton.textContent = 'Add to cart';
        }
      } else {
        this.addToCartButton.disabled = true;
        if (addToCartText) {
          addToCartText.textContent = 'Sold out';
        } else {
          this.addToCartButton.textContent = 'Sold out';
        }
      }
    }
  }

  handleNoVariant() {
    if (this.addToCartContainer && this.outOfStockContainer) {
      this.addToCartContainer.style.display = 'none';
      this.outOfStockContainer.style.display = 'block';
    }
    
    if (this.addToCartButton) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.textContent = 'Unavailable';
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }
}

// Initialize variant selector when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('🛍️ Initializing Dawn-style Variant Selector');
  
  // Small delay to ensure other scripts have loaded first
  setTimeout(() => {
    window.variantSelector = new VariantSelector();
  }, 200);
});
